// Per-agent "recent searches" list, kept in the browser (localStorage).
//
// Pure helpers: they take a Storage-like object and never touch `window`,
// so they are testable and safe in private windows where storage throws.
// Every read and write is wrapped; failing storage just means no history.

export const MAX_RECENT = 10;
const MIN_QUERY_CHARS = 2;
const MAX_QUERY_CHARS = 300;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

/** One list per signed-in agent, so a shared machine doesn't mix histories. */
export function recentKey(slackId: string | null | undefined): string {
  return `pa-recent-searches:${slackId ?? 'anon'}`;
}

/** Adds `q` at the front. Trims, ignores short or empty queries, removes
 *  an earlier copy (case-insensitive) and caps the list. */
export function pushRecent(list: readonly string[], q: string, max: number = MAX_RECENT): string[] {
  const clean = q.trim().slice(0, MAX_QUERY_CHARS);
  if (clean.length < MIN_QUERY_CHARS) return [...list];
  const lower = clean.toLowerCase();
  return [clean, ...list.filter((x) => x.toLowerCase() !== lower)].slice(0, max);
}

export function removeRecent(list: readonly string[], q: string): string[] {
  const lower = q.trim().toLowerCase();
  return list.filter((x) => x.toLowerCase() !== lower);
}

export function loadRecent(storage: StorageLike | null | undefined, key: string): string[] {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is string => typeof x === 'string' && x.trim().length >= MIN_QUERY_CHARS)
      .map((x) => x.slice(0, MAX_QUERY_CHARS))
      .slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

export function saveRecent(
  storage: StorageLike | null | undefined,
  key: string,
  list: readonly string[],
): void {
  try {
    storage?.setItem(key, JSON.stringify(list.slice(0, MAX_RECENT)));
  } catch {
    // storage full / blocked: history is a convenience, never an error
  }
}
